import {useState, useRef, useEffect} from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header.tsx";
import Footer from "@/components/layout/Footer.tsx";
import {createInquiry, uploadImageToS3} from "@/api/csApi.ts";
import {useAuth} from "@/contexts/AuthContext.tsx";

export default function InquiryWritePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isLoggedIn, isLoading } = useAuth(); // 💡 로그인 상태 가져오기
// 💡 추가: 알림창이 떴는지 기억하는 변수 (렌더링에 영향을 주지 않도록 useRef 사용)
//   const hasAlerted = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // 💡 업로드 중 버튼 비활성화를 위한 상태

  const [formData, setFormData] = useState({
    category: "",
    title: "",
    content: "",
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // 💡 비로그인 사용자 튕겨내기 (Route Guard)
  useEffect(() => {
    if (isLoading) return; // 인증 상태 확인 중이면 대기
    // 💡 로그인하지 않았고, 알림창을 아직 띄우지 않았을 때만 실행!
    // if (!isLoggedIn && !hasAlerted.current) {
    //   hasAlerted.current = true; // 이제 띄웠다고 표시
    //   alert("로그인이 필요한 서비스입니다.");
    //   navigate("/login", { replace: true });
    // }
    if (!isLoggedIn) {
      // alert 없이 조용히 고객센터 메인으로 쫓아냄 (URL 강제 입력 대비)
      navigate("/cs", { replace: true });
    }
  }, [isLoggedIn, isLoading, navigate]);

  // 폼 입력 핸들러
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 파일 첨부 핸들러
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      // 최대 3개까지만 첨부 가능하도록 제한
      if (selectedFiles.length + filesArray.length > 3) {
        alert("사진은 최대 3장까지만 첨부 가능합니다.");
        return;
      }
      setSelectedFiles((prev) => [...prev, ...filesArray]);
    }
  };

  // 첨부 파일 삭제 핸들러
  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // 폼 제출 핸들러
  // 💡 폼 제출 핸들러 (async 추가)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.category) return alert("문의 유형을 선택해 주세요.");
    if (!formData.title.trim()) return alert("제목을 입력해 주세요.");
    if (!formData.content.trim()) return alert("내용을 입력해 주세요.");

    try {
      setIsSubmitting(true); // 버튼 로딩 상태 켜기

      // 1. 선택된 사진 파일들을 하나씩 S3에 업로드 (URL 모으기)
      const uploadedImageUrls: string[] = [];
      for (const file of selectedFiles) {
        const imageUrl = await uploadImageToS3(file);
        uploadedImageUrls.push(imageUrl);
      }

      // 2. S3 URL 목록과 함께 텍스트 데이터를 백엔드로 전송
      await createInquiry({
        category: formData.category,
        title: formData.title,
        content: formData.content,
        imageUrls: uploadedImageUrls
      });

      alert("문의가 정상적으로 접수되었습니다. 담당자 확인 후 답변 드리겠습니다.");
      navigate("/cs/inquiries"); // 성공 시 내역 페이지로 이동

    } catch (error: any) {
      console.error("문의 접수 실패:", error);
      // 백엔드에서 내려준 에러 메시지가 있다면 표시, 없으면 기본 메시지
      const errMsg = error.response?.data?.message || "문의 접수 중 오류가 발생했습니다.";
      alert(errMsg);
    } finally {
      setIsSubmitting(false); // 로딩 상태 끄기
    }
  };
  // const handleSubmit = (e: React.FormEvent) => {
  //   e.preventDefault();
  //   if (!formData.category) return alert("문의 유형을 선택해 주세요.");
  //   if (!formData.title.trim()) return alert("제목을 입력해 주세요.");
  //   if (!formData.content.trim()) return alert("내용을 입력해 주세요.");
  //
  //   // TODO: 백엔드 API 연동 (FormData 객체 생성 후 POST 요청)
  //   console.log("제출 데이터:", formData);
  //   console.log("첨부 파일:", selectedFiles);
  //
  //   alert("문의가 정상적으로 접수되었습니다. 담당자 확인 후 답변 드리겠습니다.");
  //   // navigate("/cs"); // 제출 후 고객센터 메인으로 이동
  //   navigate("/cs/inquiries"); // 💡 기존 '/cs' 에서 '/cs/inquiries' 로 변경!
  // };

  // 💡 인증 확인 중이거나 비로그인 상태면 화면을 아예 그리지 않음 (깜빡임 방지)
  if (isLoading || !isLoggedIn) return null;

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <main className="flex-1 mx-auto max-w-3xl px-4 py-12 lg:px-6 mb-20 w-full">
        <div className="mx-auto max-w-3xl px-4 py-12 lg:px-6 mb-20">
          <div className="mb-8 border-b border-slate-800 pb-4">
            <h2 className="text-2xl font-black tracking-tight text-slate-800">1:1 문의하기</h2>
            <p className="mt-2 text-sm text-slate-500">
              상품, 배송, 교환/반품 등 궁금하신 점을 남겨주시면 빠르게 답변해 드리겠습니다.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 문의 유형 선택 */}
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">
                문의 유형 <span className="text-rose-600">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="h-12 w-full rounded-lg border border-slate-300 bg-white px-4 text-[15px] outline-none focus:border-slate-800 focus:ring-1 focus:ring-slate-800"
              >
                <option value="" disabled>유형을 선택해 주세요</option>
                <option value="배송">배송 문의</option>
                <option value="상품">상품/식물관리 문의</option>
                <option value="취소/교환/반품">취소/교환/반품 문의</option>
                <option value="기타">기타 문의</option>
              </select>
            </div>

            {/* 제목 입력 */}
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">
                제목 <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="제목을 입력해 주세요"
                className="h-12 w-full rounded-lg border border-slate-300 px-4 text-[15px] outline-none focus:border-slate-800 focus:ring-1 focus:ring-slate-800"
              />
            </div>

            {/* 내용 입력 */}
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">
                내용 <span className="text-rose-600">*</span>
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows={8}
                placeholder="문의하실 내용을 상세히 적어주세요. (주문번호를 남겨주시면 더 빠른 처리가 가능합니다.)"
                className="w-full resize-y rounded-lg border border-slate-300 p-4 text-[15px] outline-none focus:border-slate-800 focus:ring-1 focus:ring-slate-800"
              ></textarea>
            </div>

            {/* 사진 첨부 영역 */}
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">사진 첨부 (최대 3장)</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-12 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                  </svg>
                  이미지 선택
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
              </div>

              {/* 첨부된 파일 목록 표시 */}
              {selectedFiles.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="flex items-center justify-between rounded bg-slate-50 p-3 text-sm text-slate-600 border border-slate-100">
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="ml-4 text-slate-400 hover:text-rose-500 font-bold"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 하단 버튼 영역 */}
            <div className="mt-10 flex gap-3 pt-6 border-t border-slate-100">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={isSubmitting} // 💡 로딩 중 클릭 방지
                className="h-14 flex-1 rounded-lg border border-slate-300 bg-white text-base font-bold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting} // 💡 로딩 중 클릭 방지
                className={`h-14 flex-1 rounded-lg text-base font-bold text-white transition-colors ${
                  isSubmitting ? "bg-slate-400 cursor-not-allowed" : "bg-slate-800 hover:bg-slate-900"
                }`}
              >
                {isSubmitting ? "업로드 중..." : "문의 접수하기"} {/* 💡 텍스트 변경 */}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}